import moment from 'moment'

import { Comment } from 'modules/project_management/components/myProjects/CommentCard'

import { CustomGlobalTimeRecord } from './getCustomGlobalTimeRecordsForMyProjectsTable'

export interface WidgetCommentsProps {
  date: string
  count: number
  comments: Comment[]
}

interface IProvisionalRecord {
  projectId: number | undefined
  projectName: string | undefined
  subprojectName: string | undefined
  comment: string
  duration: number | undefined
  date: string
  isFlexible: boolean
}

export const getCustomRecordsCommentsByDate = (records: CustomGlobalTimeRecord[]) => {
  const filteredGlobalTimeRecords = filterEmptyComments(records)
  const provisionalRecords = getProvisionalRecords(filteredGlobalTimeRecords)
  const commentsGroupedByDate = groupCommentsByDate(provisionalRecords)
  const commentsWidgetObject: WidgetCommentsProps[] =
    transformCommentsIntoWidgetObjects(commentsGroupedByDate)

  const sortedCommentsWidget = commentsWidgetObject.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime()
  })

  return sortedCommentsWidget
}

const filterEmptyComments = (comments: CustomGlobalTimeRecord[]) => {
  return comments
    .map((record) => ({
      ...record,
      imputedMinutesList: record.imputedMinutesList.filter((entry) => entry.comment),
    }))
    .filter((record) => record.imputedMinutesList.length > 0)
}

const getProvisionalRecords = (comments: CustomGlobalTimeRecord[]) => {
  const provisionalRecords: IProvisionalRecord[] = comments.flatMap((comment) =>
    comment.imputedMinutesList
      .filter((list): list is typeof list & { comment: string } => Boolean(list.comment))
      .map((list) => ({
        projectId: list.flexibleId,
        projectName: comment.projectName,
        subprojectName: comment.subprojectName,
        comment: list.comment,
        duration: list.imputedMinutes,
        date: list.date,
        isFlexible: !!list.flexibleId,
      }))
  )

  return provisionalRecords
}

const groupCommentsByDate = (
  comments: IProvisionalRecord[]
): Record<string, IProvisionalRecord[]> => {
  return comments.reduce<Record<string, IProvisionalRecord[]>>((acc, comment) => {
    const date = comment.date
    acc[date] = acc[date] || []
    acc[date].push(comment)

    return acc
  }, {})
}

const transformCommentsIntoWidgetObjects = (
  commentsDate: Record<string, IProvisionalRecord[]>
): WidgetCommentsProps[] => {
  return Object.entries(commentsDate).map(([date, comments]) => ({
    date: moment(date).format('dddd, DD MMM YYYY'),
    count: comments.length,
    comments: comments.map(
      (comment): Comment => ({
        id: comment.projectId || 0,
        projectName: comment.projectName || '',
        subprojectName: comment.subprojectName,
        description: comment.comment,
        time: getDurationFomart(comment.duration || 0),
        isFlexible: comment.isFlexible,
      })
    ),
  }))
}

const getDurationFomart = (duration: number): string => {
  const durationInHours = Math.floor(duration / 60)
  const durationInMinutes = duration % 60

  if (durationInMinutes) {
    return `${durationInHours}:${durationInMinutes}`
  } else {
    return `${durationInHours}`
  }
}
